import type { Capabilities, DayEntry, DayMark, ISODate, Member, MemberId, PostDoc, PresetPicture, ProfileDoc, Session, Song } from '../contract.js';
import type { Cell } from '../grid/engine.js';

/** The view state of the app. It mirrors the prototype's state (03 §1.3) with real dates and real data. */

export type View = 'cal' | 'day' | 'person' | 'profile' | 'compose';
export type Load = 'loading' | 'loaded' | 'error';

/** Which days of one month have posts; kept while it is being refreshed. */
export interface MonthData { readonly status: Load; readonly days: Readonly<Record<ISODate, DayMark>> | null }
/** `at`: when the data arrived (ms), so data older than the media addresses' life is read again. */
export interface DayData { readonly status: Load; readonly entries: readonly DayEntry[] | null; readonly at?: number }
export interface ProfileData { readonly status: Load; readonly doc: ProfileDoc | null; readonly at?: number }

/** What is playing: one item at a time across the app (03 §9). */
export interface PlayState { readonly key: string; readonly pos: number; readonly len: number; readonly paused: boolean }

/** Live preview of a gesture on a widget (04 §1.10). */
export type Drag =
  | { readonly mode: 'move'; readonly id: string; readonly dx: number; readonly dy: number; readonly c: number; readonly r: number; readonly w: number; readonly h: number; readonly ok: boolean }
  | { readonly mode: 'resize'; readonly id: string; readonly c: number; readonly r: number; readonly w: number; readonly h: number; readonly ok: boolean }
  | { readonly mode: 'pan'; readonly id: string; readonly px: number; readonly py: number; readonly z?: number };

export interface AppState {
  /** What the data source can do; the UI hides or degrades features accordingly (06 §4). */
  readonly caps: Capabilities;

  // ---- data (06 §3.1, §5.5)
  /**
   * App start: the session is loading, ready, failed, there is no signed-in member, the signed-in
   * account has no profile yet (it chooses its name and handle first), or it is the account's first
   * sign-in (it chooses its colour first).
   */
  readonly boot: 'loading' | 'ready' | 'error' | 'signed-out' | 'needs-profile' | 'choose-colour';
  readonly session: Session | null;
  readonly months: Readonly<Record<string, MonthData>>;
  readonly days: Readonly<Record<ISODate, DayData>>;
  readonly profiles: Readonly<Record<MemberId, ProfileData>>;
  /** People met in the data (day entries, profiles) who are not in the session's member list. */
  readonly people: Readonly<Record<MemberId, Member>>;
  readonly presets: readonly PresetPicture[];
  readonly songs: readonly Song[];

  // ---- navigation
  readonly view: View;
  /** Month shown on the calendar, as 'YYYY-MM'. */
  readonly month: string;
  /** The open day. */
  readonly date: ISODate;
  readonly person: MemberId | null;
  readonly profileOf: MemberId | null;
  readonly ret: 'cal' | 'day' | 'person';

  // ---- transient phases
  readonly cd: null | 'in-pre' | 'in' | 'out-pre' | 'out';
  readonly pp: null | 'in-pre' | 'in' | 'out';
  readonly swap: 'still' | 'pre' | 'rise' | 'quick';
  readonly pfx: null | 'in-pre' | 'in';
  readonly lv: boolean;
  readonly lvKeep: boolean;
  readonly cyc: number;
  readonly slide: -1 | 0 | 1;
  readonly hover: ISODate | null;
  readonly hoverP: MemberId | null;
  readonly tray: null | 'add' | 'look';
  readonly cAsk: null | 'discard' | 'delete';
  readonly peAsk: boolean;
  readonly homeAfter: boolean;

  // ---- composer
  readonly draft: PostDoc | null;
  readonly draftBase: string;
  readonly cStatus: 'idle' | 'saving' | 'failed';
  readonly cEditing: boolean;
  readonly kDate: ISODate;
  readonly cRet: View;
  readonly cPerson: MemberId | null;
  readonly cDate: ISODate;

  // ---- profile editing
  readonly pe: ProfileDoc | null;
  readonly peBase: string;
  readonly peStatus: 'idle' | 'saving' | 'failed';

  // ---- grid editing
  readonly sel: string | null;
  readonly txt: string | null;
  readonly swOpen: boolean;
  readonly pdrag: Drag | null;
  readonly dropCell: Cell | null;
  /** Uploads still running, by widget id (04 §11.7). */
  readonly uploading: Readonly<Record<string, true>>;

  // ---- media and feedback
  readonly play: PlayState | null;
  /** Retry counters of broken photos and videos, by playback key. */
  readonly mediaRetry: Readonly<Record<string, number>>;
  readonly toast: string | null;
}

/** A partial state change, as the prototype's `setState` takes. */
export type Patch = { -readonly [K in keyof AppState]?: AppState[K] };

export function initialState(caps: Capabilities): AppState {
  return {
    caps,
    boot: 'loading', session: null, months: {}, days: {}, profiles: {}, people: {}, presets: [], songs: [],
    view: 'cal', month: '', date: '', person: null, profileOf: null, ret: 'cal',
    cd: null, pp: null, swap: 'still', pfx: null, lv: false, lvKeep: false, cyc: 0, slide: 0, hover: null, hoverP: null,
    tray: null, cAsk: null, peAsk: false, homeAfter: false,
    draft: null, draftBase: '', cStatus: 'idle', cEditing: false, kDate: '', cRet: 'cal', cPerson: null, cDate: '',
    pe: null, peBase: '', peStatus: 'idle',
    sel: null, txt: null, swOpen: false, pdrag: null, dropCell: null, uploading: {},
    play: null, mediaRetry: {}, toast: null,
  };
}
